import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import EditProfile from '../EditProfile';
import { calculateTotalPrice, calculatePrice, calculateTotalDiscount, getAuthToken, getAuthUser } from '../../../../../utils';
import Addresses from '../Addresses';
import { useCommonStyles } from '../../../../../common/common';
import { loadJWTUser } from '../../../../../store/actions/Auth';
import { AppState } from '../../../../../store';
import OrderSummary from '../OrderSummary';
import PaymentMode from '../Payment/Index';
import Login from '../../../../Register/Login';
import { Item } from '../../AddToCart';
import { CART_EMPTY } from '../../../../../constants/cartConstants';

const steps = ['SignIn', 'Delivery Addresses', 'Order Summary', 'Payment Options'];

interface CustomStepperProps {
	shopMore: any
}

const CustomStepper = (props: CustomStepperProps) => {
	const { shopMore } = props;
	const token = getAuthToken()
	const dispatch = useDispatch()
	const commonClasses = useCommonStyles()
	const { d_f_column_center } =  commonClasses
	const [activeStep, setActiveStep] = React.useState(0);
	const [completed, setCompleted] = React.useState<{
		[k: number]: boolean;
	}>({});

	const [showLogin, setShowLogin] = React.useState(false);
	const [selectedPaymentMode, setSelectedPaymentMode] = React.useState('');
	
	const { cartItems } = useSelector<AppState, AppState['cart']>(
        ({cart}) => cart,
    );
	const { user } = useSelector<AppState, AppState['auth']>(
		({ auth }) => auth,
	);
	const { shippingAddress } = useSelector<AppState, AppState['order']>(
		({ order }) => order,
	);

	useEffect(() => {
		const userDetails = getAuthUser()
		if(userDetails) {
			const parsedData = JSON.parse(userDetails)
			dispatch(loadJWTUser(parsedData._id, parsedData.isSeller))
		}
	}, [dispatch])

	useEffect(() => {
		if(token) {
			setActiveStep(1)
			setCompleted({ 0: true })
		}
	}, [token])

	const totalSteps = useCallback(() => {
		return steps.length;
	}, []);

	const completedSteps = useCallback(() => {
		return Object.keys(completed).length;
	}, [completed]);

	const allStepsCompleted = useCallback(() => {
		return completedSteps() === totalSteps();
	},[completedSteps, totalSteps]);

	useEffect(() => {
		if(allStepsCompleted()) {
			dispatch({ type: CART_EMPTY });
		}
	}, [dispatch, allStepsCompleted, activeStep])
	
	const isLastStep = () => {
		return activeStep === totalSteps() - 1;
	};

	const handleNext = () => {
		const newActiveStep =
			isLastStep() && !allStepsCompleted()
				? // It's the last step, but not all steps have been completed,
				// find the first step that has been completed
				steps.findIndex((step, i) => !(i in completed))
				: activeStep + 1;
		setActiveStep(newActiveStep);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	// const handleStep = (step: number) => () => {
	// 	setActiveStep(step);
	// };

	const handleComplete = () => {
		const newCompleted = completed;
		newCompleted[activeStep] = true;
		setCompleted(newCompleted);
		handleNext();
	};


	return (
		<Box display="flex" flexDirection="column" justifyContent="center" 
			sx={{ pl: { xs: 2, sm: 4, md: 7, lg: 10, xl: 12 }, mb: 5 }}
		>
			<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
				<Grid item xs={12} sm={8}>
					{!allStepsCompleted() &&
						<Stepper nonLinear activeStep={activeStep} orientation="vertical">
							{steps.map((label, index) => (
								<Step key={label} completed={completed[index]}
									sx={{
										'& .MuiStepLabel-root .Mui-completed': {
											color: 'success.dark', // circle color (COMPLETED)
										},
										'& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
											color: 'grey.500', // Just text label (COMPLETED)
										},
										'& .MuiStepLabel-root .Mui-active': {
											color: 'parimary.main', // circle color (ACTIVE)
										},
										'& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
											color: 'common.white', // Just text label (ACTIVE)
										},
										'& .MuiStepLabel-root .Mui-active .MuiStepIcon-text': {
											fill: 'white', // circle's number (ACTIVE)
										},
									}}
								>
									<StepLabel>
										{label === 'SignIn' && !token && label}
										{token && label === 'SignIn' && <Typography>ePridim Customer: {user && user.name}</Typography>}
										{!shippingAddress && label === 'Delivery Addresses' && label}
										{token && label === 'Delivery Addresses' && shippingAddress && <Typography>
											<b>Deliver to: </b>
											{shippingAddress && shippingAddress.address}, {shippingAddress && shippingAddress.city},  {shippingAddress && shippingAddress.postalCode}
										</Typography>}
										{token && label === 'Order Summary' && shippingAddress && <Typography>
											<b>{label}</b>
										</Typography>}
										{token && label === 'Payment Options' && shippingAddress && <Typography>
											<b>{label}</b>
										</Typography>}
									</StepLabel>
									<StepContent>
										<React.Fragment>
											{token && activeStep === 0 && <EditProfile user={user} />}
											{!token && activeStep === 0 && <>
												{!showLogin && <Box className={d_f_column_center}>
													<Box mr={1} mb={2}>Please Sign In to proceed to checkout.</Box>
														<Button
															color="primary"
															variant="contained"
															onClick={()=> setShowLogin(true)}
															style={{ marginRight: 1, textTransform: 'capitalize' }}
														>
															Sign In
														</Button>
													</Box>
												}
												{showLogin && <Login />}
												</>
											}
											{token && activeStep === 1 && <Addresses gotoNext={handleComplete} />}
											{token && activeStep === 2 && <OrderSummary gotoNext={handleComplete} />}
											{token && activeStep === 3 && 
												<PaymentMode
													selectedPaymentMode={selectedPaymentMode} 
													onSetSelectedPaymentMode={(mode: string) => setSelectedPaymentMode(mode)}
												/>
											}
											<Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
												<Button
													color="inherit"
													variant='outlined'
													disabled={activeStep === 0}
													onClick={handleBack}
													sx={{ mr: 1 }}
												>
													Back
												</Button>
												<Box sx={{ flex: '1 1 auto' }} />
												{activeStep !== steps.length && (completedSteps() === 1 || completedSteps() > 2) &&
													(
														<Button
															variant='outlined'
															disabled={(completedSteps() === totalSteps() - 1 && !selectedPaymentMode) || (label === 'Delivery Addresses' && !shippingAddress)}
															onClick={() => handleComplete()}>
															{(completedSteps() === totalSteps() - 1)
															? 'Payment'
															: 'Next' }
														</Button>
													)}
											</Box>
										</React.Fragment>
									</StepContent>
								</Step>
							))}
						</Stepper>
					}
					{allStepsCompleted() && (
						<Box sx={{p: 5}}>
							<Typography textAlign="center" sx={{ mt: 2, mb: 1 }}>
								Congratualtions, Your have placed your order successfully!
							</Typography>
							{shopMore}
						</Box>
					)}
				</Grid>
				{ !allStepsCompleted() && cartItems && cartItems.length > 0 && <Grid item xs={12} sm={4} sx={{ pr: { sm: 2, md: 5, lg: 8, xl: 10 } }}>
					<Item  sx={{textAlign: 'left', padding: '18px', boxShadow: 'none'}}>
						<Box display="flex" flexDirection="column">
							<Box component="h5"><b>Price Details</b></Box>
							<Divider sx={{marginBottom: '12px'}} />
							<Box display="flex" component="h6" flexDirection="column">
								<Box component="h6" display="flex" justifyContent="space-between"><b>Price({cartItems.length} items):</b> ₹{calculatePrice(cartItems)}</Box>
								<Box component="h6" display="flex" justifyContent="space-between"><b>Delivery Charges:</b> <Box sx={{color: '#388e3c'}}>Free</Box></Box>
								<Divider sx={{marginBottom: '12px'}} />
								<Box component="h6" display="flex" justifyContent="space-between"><b>Total Payable:</b> <b>₹{calculateTotalPrice(cartItems)}</b></Box>
								<Divider sx={{marginBottom: '12px'}} />
								<Box component="h6" sx={{color: '#388e3c', fontWeight: 'bold'}}>Your total saving on this order ₹{calculateTotalDiscount(cartItems)}</Box>
							</Box>
						</Box>
					</Item>
				</Grid>}
			</Grid>
		</Box>
	);
}

export default CustomStepper;
